import React, { useState, useEffect, useMemo } from "react";
import { Button, DataTable, KpiCard, clientSortRows } from "./ui";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem, MDBTabs, MDBTabsItem, MDBTabsLink, MDBInput } from "mdb-react-ui-kit";
import { api } from "../services";
import { useTranslation } from "react-i18next";

function CatalogOverview() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("All");
  const [viewMode, setViewMode] = useState("list");
  const [catalogs, setCatalogs] = useState([]);
  const [filteredCatalogs, setFilteredCatalogs] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("Select App");
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({
    totalCatalogs: 0,
  });

  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [tableSortColumn, setTableSortColumn] = useState("dateAdded");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    fetchApps(); // Fetch apps on component mount
    fetchCatalogs(); // Fetch all catalogs initially
  }, []);
  useEffect(() => {
    filterCatalogs();
  }, [searchQuery, selectedDate, catalogs, tableSortColumn, sortDirection, apps]);

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching apps:", err.message);
    }
  };

  const filterCatalogs = () => {
    let updatedCatalogs = [...catalogs];

    // Filter by search query
    if (searchQuery) {
      updatedCatalogs = updatedCatalogs.filter(
        (catalog) =>
          catalog.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          catalog.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by selected date
    if (selectedDate) {
      updatedCatalogs = updatedCatalogs.filter(
        (catalog) =>
          new Date(catalog.dateAdded).toISOString().split("T")[0] ===
          selectedDate
      );
    }

    updatedCatalogs = clientSortRows(
      updatedCatalogs,
      tableSortColumn,
      sortDirection,
      (catalog, col) => {
        switch (col) {
          case "dateAdded":
            return catalog.dateAdded ? new Date(catalog.dateAdded).getTime() : 0;
          case "app":
            return (
              apps.find((app) => app._id === catalog.appId)?.application || ""
            ).toLowerCase();
          default:
            return String(catalog[col] || "").toLowerCase();
        }
      }
    );

    setFilteredCatalogs(updatedCatalogs);
  };

  const fetchCatalogs = async (appId = null) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(
        appId ? `/apps/${appId}/catalogs` : "/catalogs",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { catalogs } = response.data || { catalogs: [] };
      setCatalogs(catalogs);
      setFilteredCatalogs(catalogs); // Initialize filtered catalogs
      calculateStats(catalogs);
    } catch (err) {
      console.error("Error fetching catalogs:", err.message);
    }
  };

  const calculateStats = (catalogs = []) => {
    setStats({ totalCatalogs: catalogs.length });
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === "") {
      setFilteredCatalogs(catalogs);
    } else {
      const filtered = catalogs.filter(
        (catalog) =>
          catalog.brand?.toLowerCase().includes(query) ||
          catalog.description?.toLowerCase().includes(query)
      );
      setFilteredCatalogs(filtered);
    }
  };

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleAppSelect = (app) => {
    setSelectedApp(app.application);
    fetchCatalogs(app._id); // Fetch catalogs for the selected app
  };

  const handleViewModeChange = (mode) => setViewMode(mode);

  const columns = useMemo(
    () => [
      {
        id: "brand",
        header: "Catalog Name",
        sortable: true,
        strong: true,
        render: (catalog) => catalog.brand,
      },
      {
        id: "description",
        header: "Description",
        sortable: true,
        render: (catalog) => catalog.description,
      },
      {
        id: "app",
        header: "App",
        sortable: true,
        render: (catalog) =>
          apps.find((app) => app._id === catalog.appId)?.application || "N/A",
      },
      {
        id: "dateAdded",
        header: t("createdAt"),
        sortable: true,
        render: (catalog) =>
          catalog.dateAdded
            ? new Date(catalog.dateAdded).toLocaleDateString()
            : "—",
      },
    ],
    [apps, t]
  );

  return (
    <>
      <div className="d-flex">
        <MDBContainer fluid className="p-4 content-area flex-grow-1">
          <h2 className="mb-4">Catalog Overview</h2>

          {/* Statistics Section */}
          <MDBRow className="mb-4 g-4">
            <MDBCol md="6">
              <KpiCard
                title="Total Catalogs"
                value={stats.totalCatalogs}
                accentColor="#118AB2"
                className="h-100"
              />
            </MDBCol>
          </MDBRow>

          <MDBRow className="align-items-center mb-4">
            {/* App Dropdown */}
            <MDBCol md="2">
              <MDBDropdown>
                <MDBDropdownToggle color="primary">
                  {selectedApp}
                </MDBDropdownToggle>
                <MDBDropdownMenu>
                  {apps.map((app) => (
                    <MDBDropdownItem
                      key={app._id}
                      onClick={() => handleAppSelect(app)}
                    >
                      {app.application}
                    </MDBDropdownItem>
                  ))}
                </MDBDropdownMenu>
              </MDBDropdown>
            </MDBCol>

            {/* Date Filter */}
            <MDBCol md="2">
              <MDBInput
                type="date"
                label="Filter by Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </MDBCol>

            {/* Search Bar */}
            <MDBCol md="3">
              <MDBInput
                type="text"
                label="Search Catalogs"
                value={searchQuery}
                onChange={handleSearch}
              />
            </MDBCol>

            {/* View Mode Toggle */}
            <MDBCol md="3" className="text-end">
              <Button
                color="light"
                onClick={() => setViewMode("list")}
                active={viewMode === "list"}
              >
                List
              </Button>
              <Button
                color="light"
                onClick={() => setViewMode("grid")}
                active={viewMode === "grid"}
              >
                Grid
              </Button>
            </MDBCol>
          </MDBRow>

          {viewMode === "grid" ? (
            <MDBRow>
              {filteredCatalogs?.map((catalog) => (
                <MDBCol md="4" key={catalog._id}>
                  <MDBCard className="order-card h-100">
                    <MDBCardBody className="position-relative">
                      <div>
                        <h5 className="mb-1">{catalog.brand}</h5>
                        <small className="text-muted">
                          {catalog.description}
                        </small>
                      </div>
                      <div className="text-center my-3">
                        <h6>
                          {catalog.charges} {catalog.currency}
                        </h6>
                        <small className="text-muted">{catalog.location}</small>
                      </div>
                    </MDBCardBody>
                  </MDBCard>
                </MDBCol>
              ))}
            </MDBRow>
          ) : (
            <DataTable
              tableId="catalog-overview"
              title={t("catalog")}
              selectionMode="index"
              columns={columns}
              data={filteredCatalogs || []}
              rowKey="_id"
              emptyState="No catalogs to display"
              sort={{
                column: tableSortColumn,
                direction: sortDirection,
                onChange: ({ column, direction }) => {
                  setTableSortColumn(column);
                  setSortDirection(direction);
                },
              }}
            />
          )}
        </MDBContainer>
      </div>
    </>
  );
}

export default CatalogOverview;
